/**
 * The grid selection as the agent sees it (F-144): the row and column ids
 * inside the anchor…head rectangle, in sheet order — what "these rows" and
 * "this column" resolve to on the server.
 */
import { AGENT_MAX_SELECTION_IDS, type AgentSelection, type BoardDoc, type CellRangeSelection } from "@kola/shared";

export function agentSelection(
  board: BoardDoc,
  selection: CellRangeSelection | null | undefined,
  sheetId: string,
): AgentSelection | undefined {
  if (!selection || selection.sheetId !== sheetId) return undefined;
  const rowIds = between(board.sortedRowIds(sheetId), selection.anchor.rowId, selection.head.rowId);
  const colIds = between(board.sortedColIds(sheetId), selection.anchor.colId, selection.head.colId);
  if (rowIds.length === 0 || colIds.length === 0) return undefined;
  // Past the cap the selection is "the sheet" — the model reads it instead.
  if (rowIds.length > AGENT_MAX_SELECTION_IDS || colIds.length > AGENT_MAX_SELECTION_IDS) return undefined;
  return { rowIds, colIds };
}

function between(ordered: readonly string[], a: string, b: string): string[] {
  const ia = ordered.indexOf(a);
  const ib = ordered.indexOf(b);
  if (ia === -1 || ib === -1) return [];
  const [from, to] = ia <= ib ? [ia, ib] : [ib, ia];
  return ordered.slice(from, to + 1);
}

/** "3 rows × 2 columns" for the composer's hint. */
export function describeSelection(selection: AgentSelection | undefined, board: BoardDoc, sheetId: string): string | null {
  if (selection === undefined) return null;
  const rows = `${selection.rowIds.length} ${selection.rowIds.length === 1 ? "row" : "rows"}`;
  const names = selection.colIds.map((id) => board.getColumn(sheetId, id)?.name ?? "").map((n) => (n === "" ? "(untitled)" : n));
  const cols = names.length <= 3 ? names.map((n) => `"${n}"`).join(", ") : `${names.length} columns`;
  return `${rows} × ${cols}`;
}
